
import { logger } from "@/utils/logger";
import { Options, Vue } from "vue-class-component";
import { Emit } from "vue-property-decorator";
import ThirdSearchDialog from "@/components/demand/dialogs/ThirdSearchDialog.vue";
import Comment from "@/components/communication/Comment.vue";
import PartyFinancialSituation from "@/components/middle-office/tabs/party/organization/PartyFinancialSituation.vue";
import TaskPartyClassification from "@/components/middle-office/tabs/party/TaskPartyClassification.vue";
import store from "@/store";
import {
  BirthChildren,
  businessDataUtils,
  customRoles,
  ExpenseIncome,
  Facility,
  financialExposures,
  Jobs,
  PartyAddress,
  PartyContactMechanism,
  PartyCorrespondent,
  Person,
  PersonMaritalStatus,
  PicklistItem,
  PostalCode,
  RevenuesAnalysis,
  SimpleQuote,
  Third,
  ThirdType
} from "@/types";
import {
  add,
  addExpandedItem,
  edit,
  getMaskFormat,
  getMaskInput,
  globalConfig,
  phoneRules,
  remove,
  removeExpandedItem,
  OFFER_TAX,
  isCustomerApeNotEligible,
  applyLldAxaGlobalEncours,
  getSelectedProposalItem,
  getPartyRoleFromOffer,
  isEncoursAxaGlobal,
  isLldFinancingProduct,
  resolveOfferForLld,
} from "@/utils";
import { applyPersonNationalityFlag } from "@/utils/nationalityFlag";
import {
  formatDateLocale,
  formatDateService,
  getSirenMask,
  getYearsOfDate,
  isValidDate,
} from "@/utils/configuration/formatters-config";
import i18n from "@/i18n";
import { toUpperCase } from "uri-js/dist/esnext/util";
import { useRoute } from "vue-router";
import moment from "moment";
import { ref } from "vue";
import twCurrencyInput from "@/components/general/twCurrencyInput.vue";
import twPercent from "@/components/general/twPercent.vue";
import { currencyConfiguration } from "@/utils/configuration/currencyInput-config";
import PartyAccount from "@/components/middle-office/tabs/party/person/PartyAccount.vue";
import TaskPartyRelation from "@/components/middle-office/tabs/party/TaskPartyRelation.vue";
import PartyProDetails from '@/components/middle-office/tabs/party/person/PartyProDetails.vue'
import { checkOrganization } from "@/store/services/offerService";

const FOREIGN_BIRTH_POSTAL_CODE = "99999";

@Options({
  methods: {
    toUpperCase,
    isCorrespondentForeignBirth(c: any) {
      return String(c?.businessData?.birthplaceZipCode ?? "") === FOREIGN_BIRTH_POSTAL_CODE;
    },
    getBirthplaceDisplay(c: any) {
      if (!c?.businessData) {
        return "";
      }
      const zip = c.businessData.birthplaceZipCode != null
        ? String(c.businessData.birthplaceZipCode)
        : "";
      if (!zip) {
        return "";
      }
      if (zip === FOREIGN_BIRTH_POSTAL_CODE) {
        return FOREIGN_BIRTH_POSTAL_CODE;
      }
      const city = c.businessData.birthplaceCity;
      return city ? `${zip} - ${city}` : zip;
    },

    onBirthplaceChange(c: any, selectedValue: any) {
      if (!c?.businessData) {
        return;
      }
      const selected = this.postalCodes.find((opt: any) => opt.value === selectedValue)
        || this.postalCodes.find((opt: any) => opt.label === selectedValue)
        || this.postalCodes.find((opt: any) => String(opt.config?.postal_code) === String(selectedValue));
      if (!selected?.config) {
        return;
      }
      const zip = String(selected.config.postal_code ?? "");
      c.businessData.birthplaceZipCode = zip;
      const countryCode = selected.config.country?.id || selected.config.country?.value || "";
      if (zip === FOREIGN_BIRTH_POSTAL_CODE) {
        c.businessData.birthplaceCity = "";
        if (c.businessData.birthplaceCountry == null) {
          c.businessData.birthplaceCountry = "";
        }
        return;
      }
      c.businessData.birthplaceCity = selected.config.city;
      c.businessData.birthplaceCountry = countryCode || "FR";
    },
    normalizeCorrespondentBirthplaceOnGet(correspondent: any) {
      const businessData = correspondent?.businessData;
      if (!businessData) {
        return;
      }
      if (businessData.birthplaceCountry == null) {
        businessData.birthplaceCountry = "";
      }
      if (businessData.birthplaceZipCode == null || businessData.birthplaceZipCode === "") {
        return;
      }
      businessData.birthplaceZipCode = String(businessData.birthplaceZipCode);
      if (businessData.birthplaceZipCode === FOREIGN_BIRTH_POSTAL_CODE) {
        if (businessData.birthplaceCity == null) {
          businessData.birthplaceCity = "";
        }
        return;
      }
      if (!businessData.birthplaceCountry) {
        const birthplaceLabel = businessData.birthplaceCity
          ? `${businessData.birthplaceZipCode} - ${businessData.birthplaceCity}`
          : "";
        const matchedBirthplace = this.postalCodes.find(
          (opt: any) => opt.label === birthplaceLabel
            || String(opt.config?.postal_code) === businessData.birthplaceZipCode
        );
        businessData.birthplaceCountry =
          matchedBirthplace?.config?.country?.id
          || matchedBirthplace?.config?.country?.value
          || "FR";
      }
    },
    getResidenceDisplay(c: any) {
      if (c && c.businessData) {
        const city = c.businessData.city
        const zip = c.businessData.zipCode
        return city && zip ? `${zip} - ${city}` : ''
      }
    },

    onResidenceChange(c: any, selectedValue: any) {
      if (c && c.businessData) {
        const selected = this.postalCodes.find((opt: any) => opt.value === selectedValue)
        if (selected?.config) {
          c.businessData.city = selected.config.city
          c.businessData.zipCode = selected.config.postal_code
          const countryCode = selected.config.country?.id || selected.config.country?.value
          c.businessData.country = countryCode
            ? this.$t("demand.party.country." + countryCode)
            : (selected.config.country?.label || selected.config.country?.name || '')
        }
      }
    },
    addCorrespondent(initCorrespondent: any) {
      if (this.party.type.id == ThirdType.PERSON) {
        this.party.person.correspondents.push(initCorrespondent);
      }
      else {
        this.party.organization.correspondents.push(initCorrespondent);
      }
      this.correspondentsList;
    },
    filterPostalCodesRealTime(val: string, update: any) {
      update(() => {
        if (val === '') {
          this.filteredPostalCodes = this.postalCodes;
        } else {
          const needle = val.toLowerCase();
          this.filteredPostalCodes = this.postalCodes.filter(
            (v: any) => v.label.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    },
    filterPicklistByCodeOrLabel(
      val: string,
      update: (fn: () => void) => void,
      source: any[],
      assign: (options: any[]) => void
    ) {
      update(() => {
        if (!val) {
          assign(source);
          return;
        }
        const needle = val.toLowerCase().trim();
        assign(
          source.filter((item: any) => {
            const code = (item.value ?? "").toString().toLowerCase();
            const label = (item.label ?? "").toLowerCase();
            return code.includes(needle) || label.includes(needle);
          })
        );
      });
    },
    filterActivitySector(val: string, update: (fn: () => void) => void) {
      this.filterPicklistByCodeOrLabel(
        val,
        update,
        this.activitySector,
        (options: any[]) => {
          this.filteredActivitySectorOptions = options;
        }
      );
    },
    filterLegalCategory(val: string, update: (fn: () => void) => void) {
      this.filterPicklistByCodeOrLabel(
        val,
        update,
        this.legalCategory,
        (options: any[]) => {
          this.filteredLegalCategoryOptions = options;
        }
      );
    },
    filterNaceOptions(val: string, update: (fn: () => void) => void) {
      this.filterPicklistByCodeOrLabel(
        val,
        update,
        this.naceOptions,
        (options: any[]) => {
          this.filteredNaceOptions = options;
        }
      );
    },
    filterSocioProfessionalCategory(val: string, update: (fn: () => void) => void) {
      this.filterPicklistByCodeOrLabel(
        val,
        update,
        this.socioProfessionalCategory,
        (options: any[]) => {
          this.filteredSocioProfessionalCategoryOptions = options;
        }
      );
    },
    normalizeSpaces(val: any) {
      return (val || '').trim().replace(/\s+/g, ' ')
    },

    getCptInput1(c: any) {
      if (!this.isLegacyCpt(c)) {
        return c.familyName || ''
      }

      if (c.__cptInput1 !== undefined) {
        return c.__cptInput1
      }

      this.initializeCptDisplay(c)
      return c.__cptInput1 || ''
    },

    getCptInput2(c: any) {
      if (!this.isLegacyCpt(c)) {
        return c.firstName || ''
      }

      if (c.__cptInput2 !== undefined) {
        return c.__cptInput2
      }

      this.initializeCptDisplay(c)
      return c.__cptInput2 || ''
    },

    setCptInput1(c: any, val: any) {
      const value = this.normalizeSpaces(val)

      if (!this.isLegacyCpt(c)) {
        c.familyName = value
        return
      }

      c.__cptInput1 = value
      this.syncCptModel(c)
    },

    setCptInput2(c: any, val: any) {
      const value = this.normalizeSpaces(val)

      if (!this.isLegacyCpt(c)) {
        c.firstName = value
        return
      }

      c.__cptInput2 = value
      this.syncCptModel(c)
    },

    initializeCptDisplay(c: any) {
      if (!this.isLegacyCpt(c)) return

      const first = this.normalizeSpaces(c.firstName)
      const family = this.normalizeSpaces(c.familyName)

      const hasDuplicatedName =
        first &&
        family &&
        first.toLocaleLowerCase() === family.toLocaleLowerCase()

      const combined = hasDuplicatedName ? first : `${first} ${family}`.trim()
      const parts = combined ? combined.split(' ') : []

      c.__cptInput1 = combined
      c.__cptInput2 = ''

/*      if (parts.length === 2) {
        c.__cptInput1 = combined
        c.__cptInput2 = ''
        return
      }

      c.__cptInput1 = first
      c.__cptInput2 = family*/
    },

    normalizeCptContacts(list: any[]) {
      list.forEach((c: any) => {
        if (this.isLegacyCpt(c)) {
          this.initializeCptDisplay(c)
        }
      })
    },

    syncCptModel(c: any) {
      if (!this.isLegacyCpt(c)) {
        return
      }

      const input1 = this.normalizeSpaces(c.__cptInput1)
      const input2 = this.normalizeSpaces(c.__cptInput2)

      if (input2) {
        c.firstName = input1
        c.familyName = input2
        return
      }

      if (!input1) {
        c.firstName = ''
        c.familyName = ''
        return
      }

      const parts = input1.split(' ')

      if (parts.length === 1) {
        c.familyName = parts[0] || ''
        c.firstName = ''
        return
      }

      c.familyName = parts[0] || ''
      c.firstName = parts.slice(1).join(' ')
    },
    isLegacyCpt(c: any) {
      return c?.qualificationType?.type?.value === 'CPT' && !c?.businessData
    },
  },
  data() {
    return {
      selectedCity: '',
      selectedTypes: [],
      addressOptions: [
        { value: 'SIEGE', label: 'Siège social' },
      ],
      filteredPostalCodes: [],
      filteredActivitySectorOptions: [],
      filteredLegalCategoryOptions: [],
      filteredNaceOptions: [],
      filteredSocioProfessionalCategoryOptions: [],
    }
  },
  mounted() {
    this.filteredPostalCodes = this.postalCodes;
    this.filteredActivitySectorOptions = this.activitySector;
    this.filteredLegalCategoryOptions = this.legalCategory;
    this.filteredNaceOptions = this.naceOptions;
    this.filteredSocioProfessionalCategoryOptions = this.socioProfessionalCategory;

    if (this.correspondents) {
      const c = this.correspondents.find(
        (item: any) => item?.businessData?.birthplaceCity
      );
      this.selectedCity = this.getCodeCity(c);
    }

    if (this.correspondents) {
      this.correspondents.forEach((correspondent: any) => {
        if (correspondent.businessData?.country) {
          const countryCode = correspondent.businessData.country;
          if (countryCode.length <= 3) {
            correspondent.businessData.country = this.$t("demand.party.country." + countryCode);
          }
        }
        this.normalizeCorrespondentBirthplaceOnGet(correspondent);
      });

      this.normalizeCptContacts(this.correspondents);
    }
  },
  components: {
    TaskPartyRelation,
    ThirdSearchDialog,
    Comment,
    PartyFinancialSituation,
    TaskPartyClassification,
    twCurrencyInput,
    twPercent,
    PartyAccount,
    PartyProDetails
  },
  props: ["party", "associatedParty", "role", "associatedPartyId", "tabPosition"],
  created() {
    this.ensurePersonBirthplaceStructure(this.party?.person);
    this.ensurePersonBirthplaceStructure(this.manager);
    if (this.party?.businessData?.renewal && typeof this.party.businessData.renewal === 'string') {
      this.party.businessData.renewal = parseInt(this.party.businessData.renewal);
    }
    if (this.party?.businessData?.isCfClient && typeof this.party.businessData.isCfClient === 'string') {
      this.party.businessData.isCfClient = parseInt(this.party.businessData.isCfClient);
    }
    const siege = this.addressOptions.find((opt: any) => opt.value === 'SIEGE')  // Trouver et sélectionner "Siège Social" par défaut
    if (siege) {
      this.selectedTypes = [siege.value]
    }
    this.ensureOrganizationTvaIntracom();
    this.applyLldEncoursGlobal();
  },
  watch: {
    "$i18n.locale"() {
      if (this.party.type.id) {
        this.party.type.label = i18n.global.t(
          "enum.party.type." + this.party.type.id
        );
      }
    },
    "party.person.birthDate"(newValue, oldValue) {
      if (oldValue) {
        this.dispatchUpdatePartyInsurance();
      }
    },
    "party.person": {
      handler(person) {
        this.ensurePersonBirthplaceStructure(person);
        applyPersonNationalityFlag(person);
      },
      immediate: true,
    },
    "party.organization": {
      handler() {
        this.ensureOrganizationTvaIntracom();
      },
      immediate: true,
    },
    correspondentsList: {
      handler(list: any[]) {
        list?.forEach((correspondent: any) => {
          this.normalizeCorrespondentBirthplaceOnGet(correspondent);
        });
      },
      immediate: true,
    },
    "party.person.financialExposures": {
      handler(newValue) {
        if (Array.isArray(newValue) && newValue.length === 0) {
          this.addExpandedExpossure();
        }
        this.applyLldEncoursGlobal();
      },
      deep: true,
    },
    "party.organization.financialExposures": {
      handler() {
        this.applyLldEncoursGlobal();
      },
      deep: true,
    },
    "party.person.taxGeographicZone": function (newValue) {
      this.handleTaxGeographicZone(newValue);
    },
    "party.organization.taxGeographicZone": function (newValue) {
      this.handleTaxGeographicZone(newValue);
    },
    "party.type"() {
      store.dispatch("financingModule/refreshStateChangePartyType");
    },
    "party.organization.activitySector.type.value"(newValue) {
      const selected = this.activitySector.find((o: any) => o.value === newValue);
      if (selected) {
        this.party.organization.activitySector.flagEligible = selected.flagEligible;
      } else {
        this.party.organization.activitySector.flagEligible = null;
      }
    },
    activitySector(options: any[]) {
      this.filteredActivitySectorOptions = options;
    },
    legalCategory(options: any[]) {
      this.filteredLegalCategoryOptions = options;
    },
    naceOptions(options: any[]) {
      this.filteredNaceOptions = options;
    },
    socioProfessionalCategory(options: any[]) {
      this.filteredSocioProfessionalCategoryOptions = options;
    },
    typeThird(value: any) {
      this.openDialog = value;
    },
    "party.person.birthplace.type.config.country.id": function (newValue) {
      this.country = newValue;
      const person = this.party?.person;
      if (person) {
        this.ensurePersonBirthplaceStructure(person);
        if (
          this.postalCodes &&
          this.postalCodes.filter((f: any) => f.config?.country?.id === newValue)
            .length === 1
        ) {
          let opt = this.postalCodes?.filter(
            (f: any) => f.config?.country?.id == this.country
          )[0];
          if (opt?.config) {
            person.birthplace.type = {
              value: opt.value,
              label: opt.label,
              config: {
                city: opt.config.city,
                department: opt.config.department,
                country: {
                  id: newValue,
                  label: opt.config.country?.label ?? "",
                },
              },
            };
          }
        } else if (person.birthplace?.type?.config?.country) {
          person.birthplace.type.config.country.id = newValue;
          person.birthplace.type.config.city = "";
          person.birthplace.type.config.department = "";
          if (person.birthplace.type) {
            person.birthplace.type.label = "";
          }
        }
        const personData: any = person;
        if (!personData.businessData) {
          personData.businessData = {};
        }
        delete personData.businessData.birthplaceCity;
        if (newValue && newValue !== "FR") {
          personData.businessData.birthplaceCountry = newValue;
          if (personData.birthplace) {
            personData.birthplace.resourceUid = "";
            personData.birthplace.postalCode = "";
            personData.birthplace.department = "";
            if (personData.birthplace.country) {
              personData.birthplace.country = {
                name: "",
                objectType: "odm.party.country",
              };
            }
          }
        } else {
          delete personData.businessData.birthplaceCountry;
        }
      }
    },
    "manager.birthplace.type.config.country.id": function (newValue) {
      this.managerCountry = newValue;
      if (
        this.postalCodes &&
        this.postalCodes.filter((f: any) => f.config.country.id === newValue)
          .length === 1
      ) {
        let opt = this.postalCodes?.filter(
          (f: any) => f.config.country.id == this.managerCountry
        )[0];
        if (this.manager && this.manager.birthplace) {
          this.manager.birthplace.type = {
            value: opt.value,
            label: opt.label,
            config: {
              city: opt.config.city,
              department: opt.config.department,
              country: {
                id: newValue,
                label: opt.config.country.label,
              },
            },
          };
        }
      } else {
        if (this.manager && this.manager.birthplace && this.manager.birthplace.type
          && this.manager.birthplace.type.config && this.manager.birthplace.type.id) {
          this.manager.birthplace.type.config.country.id = newValue
        }
      }
    },
  },
  computed: {
    matchedSGThirdParty() {
      const targetSiret = this.role?.businessData?.siretSG
      if (!targetSiret) return null

      const associatedParties =
        this.$store.state.taskModule?.offer?.associatedParties ?? []
      const firstMatch = associatedParties.find((associatedParty: any) =>
        associatedParty?.associatedParty?.third?.facilities?.some(
          (facility: any) => facility?.siret === targetSiret
        )
      )
      return firstMatch?.associatedParty?.third ?? null
    },
    isPartyOrganizationEligible: function () {
      return isCustomerApeNotEligible(store.state.taskModule.offer)
    },
    isIdentifyDisabled: function () {
      return !this.checkEditButtonIdentify;
    },

    isPersonBtnDisabled: function () {
      return this.personBtn;
    },
    isAddressBtnDisabled: function () {
      return this.addressBtn;
    },
    isIdentityDocBtnDisabled: function () {
      return this.identityDocBtn;
    },
    isPersonPhysique: function () {
      return this.party.type.id == ThirdType.PERSON;
    },
    isPersonMorale: function () {
      return this.party.type.id == ThirdType.ORGANIZATION;
    },
    isCommunicationPlusBtnDisabled: function () {
      return this.communicationPlusBtn;
    },

    correspondentsList: function () {
      if (this.party.type.id == ThirdType.PERSON) {
        this.correspondents = this.party.person.correspondents;
      } else {
        this.correspondents = this.party.organization.correspondents
      }

      return [...this.correspondents];
    },
    isJobsBtnDisabled: function () {
      return this.personJobsBtn;
    },
    isSituationDisabled: function () {
      return this.situationBtn;
    },
    situationCanBeEdited: function () {
      return !this.checkEditSituation;
    },
    isAnalysisBtnDisabled: function () {
      return !this.analysisBtn;
    },
    isExposureBtnDisabled: function () {
      return !this.ExpossureBtn;
    },
    isCompanyPlusBtnDisabled: function () {
      return !this.companyPlusBtn;
    },
    addressesList: function () {
      if (this.party.type.id == ThirdType.PERSON) {
        this.addresses = this.party.person?.addresses;
      } else {
        this.addresses = this.party?.organization.addresses;
      }
      return this.addresses;
    },
    facilitiesList: function () {
      if (this.party.type.id == ThirdType.ORGANIZATION) {
        this.facilities = this.party.organization.facilities;
      }
      return this.facilities;
    },

  },
})
export default class DemandTabThird extends Vue {
  party?: Third;
  tabPosition?: number;
  correspondents: PartyCorrespondent[] = [];
  facilities: Facility[] = [];
  ExpossureBtn = true;
  ExpossureItemPlusBtn = false;
  financialExposures: any[] = [];
  name = "DemandTabThird";
  openDialog = "";
  dateMask = "DD/MM/YYYY";
  country = "";
  facilitiesOpened = false;
  companyPlusBtn = false;
  managerCountry = "";
  managerPostalCode = "";
  siretMask = "#####";
  businessDataUtils = businessDataUtils;
  a = "";
  c: any;
  add = add;
  remove = remove;
  edit = edit;
  addExpandedItem = addExpandedItem;
  removeExpandedItem = removeExpandedItem;
  maskFormat = getMaskFormat();
  maskInput = getMaskInput();

  showDialog?: boolean = false;
  showManagerDialog = false;
  checkEditButtonIdentify = true;
  checkEditButtonManager = true;
  personBtn = false;
  checkEditIdentify = true;
  checkEditSignatureMethod = false;
  checkEditManager = false;
  addressBtn = false;
  communicationPlusBtn = false;
  personJobsBtn = false;
  situationBtn = false;
  checkEditSituation = false;
  analysisBtn = true;
  addresses: Array<PartyAddress> = [];
  identityOpened = true;
  signatureMethodOpened = false;
  managerOpened = true;
  associatedParty?: any;
  role?: any;
  selectedPerson = true;
  globalConfig: any = globalConfig;
  phoneContact = store.getters["demandModule/getContactMechanismInit"];
  associatedPartyId!: number
  customRoles = customRoles;
  identityDocBtn = false;
  phoneRules = phoneRules;
  removeContacts(index: number, contacts: Array<PartyContactMechanism>) {
    if (contacts) {
      store.state.demandModule?.deletedContacts?.push(contacts[index]);
      contacts.splice(index, 1);
    }
  }

  validateStreetNum(index: number, val: any) {
    if (val && val < 0) {
      if (this.addresses) {
        this.addresses[index].address.streetNum = 0;
      }
    }
  }
  updateSigningAuthority(selectedCorrespondent: PartyCorrespondent) {
    if (selectedCorrespondent.signingAuthority) {
      this.correspondents.forEach(correspondent => {
        if (correspondent !== selectedCorrespondent) {
          correspondent.signingAuthority = false;
        }
      });
    }
  }
  addExpandDocumentIdentity(items: Jobs[]) {
    if (items && items.length === 0) {
      const element = store.getters["thirdModule/getThirdIdProofInit"];
      items.push(element);
    }
  }
  get currencyConfiguration() {
    return currencyConfiguration;
  }

  get isPartPro() {
    return store.getters["demandModule/getPartProExist"];
  }
  ensureOrganizationTvaIntracom() {
    const organization = this.party?.organization as any;
    if (!organization) {
      return;
    }
    if (!organization.businessData || typeof organization.businessData !== "object") {
      organization.businessData = { TVA_intracom: "" };
    } else if (organization.businessData.TVA_intracom === undefined) {
      organization.businessData.TVA_intracom = "";
    }
  }
  get facilityType() {
    return store.getters['picklistModule/facilityTypeItems']
  }
  get yesOrNoOptions() {
    return store.getters['picklistModule/getYesorNoOptions']
  }
  get expossureList() {
    if (this.party?.type?.id == ThirdType.PERSON) {
      return this.party?.person?.financialExposures;
    } else {
      return this.party?.organization?.financialExposures;
    }
  }
  get documentIdentity() {
    return this.party?.person?.ids;
  }
  get taskDefinition() {
    return store.getters["taskModule/taskDefinition"];
  }
  get FR() {
    return {
      days: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      daysShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
      months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
      monthsShort: ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc']
    }
  }
  changePostalCodePerson(index: number, value: any) {
    if (this.party && this.party.person && this.party.person.jobs) {
      if (this.party.person.jobs[index]) {
        if (this.party.person.jobs[index].jobLocation) {
          this.party.person.jobs[index].jobLocation.postalCode!.city =
            value.config.city;
          this.party.person.jobs[index].jobLocation.postalCode!.country.name =
            value.config.country.label;
          this.party.person.jobs[index].jobLocation.postalCode!.postalCode =
            value.value;
          this.party.person.jobs[index].jobLocation.postalCode!.type = {
            value: value.id,
            label: value.config.city,
          };
        }
      }
    }
  }

  private static populatePostalCode(value: PostalCode, event: PicklistItem) {
    value.city = event.config.city;
    if (value.country) {
      value.country.name = event.config.country.label;
    }
    value.postalCode = event.id;
  }

  formatDateFrom(value: any) {
    value.validity.from = value.validity.from.includes("/")
      ? formatDateLocale(value.validity.from, "DD/MM/YYYY", "fr", "DD/MM/YYYY")
      : formatDateLocale(value.validity.from, "DDMMYYYY", "fr", "DD/MM/YYYY");
  }
  formatDateUntil(value: any) {
    value.validity.until = value.validity.until.includes("/")
      ? formatDateLocale(value.validity.until, "DD/MM/YYYY", "fr", "DD/MM/YYYY")
      : formatDateLocale(value.validity.until, "DDMMYYYY", "fr", "DD/MM/YYYY");
  }

  formatDate(date: string) {
    return formatDateLocale(date, this.dateMask, "fr", "YYYY/MM/DD");
  }


  removeExpanded(exposureToRemove: any) {
    const index = this.expossureList?.findIndex((e) => e === exposureToRemove);
    if (index !== undefined && index !== -1) {
      this.expossureList?.splice(index, 1);
    }
  }

  addExpandedExpossure(items: any[]) {
    if (!!items && items.length === 0) {
      const element = store.getters["thirdModule/getExpossureInit"];
      element.contractReference = this.offer?.reference;
      this.expossureList?.push(element);
    }
  }

  addExpossure() {
    const element = store.getters["thirdModule/getExpossureInit"];
    element.contractReference = this.offer?.reference;
    this.expossureList?.push(element);
  }

  onFinancialInstitutionChange(exposure: any, financialInstitutionValue: string) {
    if (financialInstitutionValue !== 'CF' && financialInstitutionValue !== 'AFF_LIEE' ) {
      return;
    }
    if (!exposure.outstandingType) {
      exposure.outstandingType = { value: '', label: '' };
    }
    exposure.outstandingType.value = 'OTHER';
  }

  onChangeFlagFinancialPool(value: any, event: any) {
    if (!event.target) {
      value.shareFinancialPool = null;
    }
  }

  onChangeFlagRiskPool(value: any, event: any) {
    if (!event.target) {
      value.shareRiskPool = null;
    }
  }

  removeExpandedDocIdentity(items: any[]) {
    if (!!items && items.length === 1) {
      items.slice(0, 1);
    }
  }

  get third() {
    return store.state.thirdModule.third;
  }
  get scaleSelected() {
    return store.state.financingModule.scalesModule.scaleSelected;
  }
  get proposalAssets() {
    return store.state.demandModule.assetState.proposalAssets;
  }
  get startDate() {
    if (store.state.demandModule.offer?.validity) {
      const startDate = store.state.demandModule.offer?.validity.from;
      return startDate
        ? startDate
        : ref(formatDateService(new Date(), "-")).value;
    }
  }

  get phonesListSize() {
    return this.contacts?.filter(
      (con) =>
        con.contactMechanism &&
        con.contactMechanism.type?.id?.includes("PHONE_CONTACT")
    ).length;
  }
  updateQualificationType(correspondent: any, value: any) {
    if (!correspondent.qualificationType) {
      correspondent.qualificationType = { type: {} };
    } else if (!correspondent.qualificationType.type) {
      correspondent.qualificationType.type = {};
    }
    correspondent.qualificationType.type.value = value;
  }
  updateCodeQualificationTypeGroupe(value: string | null | undefined) {
    const organization = this.party?.organization;
    if (!organization) return;

    if (!value) {
      organization.codeQualificationType = {
        type: { value: "", label: "" },
      };
      return;
    }
    const option = this.qualificationTypeGroupeOptions.find(
      (item: any) => item.value === value
    );
    organization.codeQualificationType = {
      type: {
        value,
        label: option?.label ?? "",
      },
    };
  }
  handleTaxGeographicZone(geographicalZone: string) {
    if (this.scaleSelected?.taxOnInterest && geographicalZone) {
      const countrySet = this.determineCountry() || 'FRA'
      const requestBody = {
        objectType: "",
        systemUid: "odm-product",
        resourceUid: "",
        businessData: {},
        geographicZone: geographicalZone,
        country: countrySet,
        evaluationDate: this.startDate,
        daaq: "",
      };
      store.dispatch("demandModule/setEvaluatedTaxRate", {
        taxCode: this.scaleSelected?.taxOnInterest,
        requestBody,
        callback: (result: any) => {
          if (this.scaleSelected?.productTax)
            this.scaleSelected.productTax.fixedValue = result[0].taxRate;
        },
      });
    }
  }

  determineCountry() {
    if (this.third?.type?.id?.includes("Organization")) {
      if (this.third?.organization?.addresses)
        return this.third?.organization?.addresses[0]?.address?.postalCode
          ?.country?.resourceUid;
    } else {
      if (this.third?.addresses)
        return this.third?.addresses[0]?.address?.postalCode?.country
          ?.resourceUid;
    }
  }
  calculateAge(dateBirth: string, person: Person): number | undefined {
    if (dateBirth !== "") {
      person.age = getYearsOfDate(
        formatDateLocale(dateBirth, "DD/MM/YYYY", "us", "YYYY-MM-DD")
      );
    }
    return person.age;
  }
  calculateExposureGrossOutstanding(value: any): void {
    if (!value?.netOutstanding) return;
    const isOrganisation = checkOrganization(this.party?.type);
    const grossValue = String(value.netOutstanding * OFFER_TAX);

    if (isOrganisation && this.party?.organization?.financialExposures) {
      const exposure = this.party.organization.financialExposures.find(
        (item) => item === value
      );
      if (exposure) exposure.grossOutstanding = grossValue;
    } else if (!isOrganisation && this.party?.person?.financialExposures) {
      const exposure = this.party.person.financialExposures.find(
        (item) => item === value
      );
      if (exposure) exposure.grossOutstanding = grossValue;
    }
  }

  calculateChildrenAge(birthChildren: BirthChildren, person: Person): void {
    if (
      birthChildren.dateOfBirthChildren &&
      isValidDate(birthChildren.dateOfBirthChildren, "DD/MM/YYYY", "fr")
    ) {
      birthChildren.childrenAge = moment().diff(
        formatDateLocale(
          birthChildren.dateOfBirthChildren,
          "DD/MM/YYYY",
          "us",
          "YYYY-MM-DD"
        ),
        "years"
      );
    }
  }

  @Emit("changeDone")
  changeDone(done: boolean) {
    return done;
  }
  get currentRoute() {
    return useRoute().name;
  }
  updateContactList() {
    this.contacts?.push(this.phoneContact);
  }

  roundNumber(event: any, person: Person, number: any) {
    globalConfig.input_rules.ToFixNumber(event, person, number);
  }

  onNationalityChange(countryCode: string | null, person: Person) {
    applyPersonNationalityFlag(person, countryCode);
  }

  changePostalCodeIdentity(item: any, person: Person) {
    const postalCode = {
      config: item.config,
      label: item.label,
      value: item.value
    }
    if (person.birthplace) {
      person.birthplace.postalCode = postalCode;
      person.birthplace.country = this.country.toString;
      person.birthplace.department = item.config?.department?.toString();
    }
  }
  changePostalCodeManager(item: any, person: Person) {
    if (person.birthplace) {
      person.birthplace.postalCode = item.value;
      person.birthplace.country = this.managerCountry.toString;
    }
  }
  changePostalCodePM(value: PostalCode, event: PicklistItem, index: number) {
    DemandTabThird.populatePostalCode(value, event);
    if (this.facilities && this.facilities[index].address) {
      this.facilities[index].address!.postalCode = value;
    }
  }

  changePostalCode(index: number, value: any) {
    if (this.addresses) {
      this.addresses[index].address.postalCode!.city = value.config.city;
      this.addresses[index].address.postalCode!.country!.name =
        value.config.country.label;
      this.addresses[index].address.postalCode!.postalCode = value.value;
      this.addresses[index].address.postalCode!.type = {
        value: value.id,
        label: value.config.city,
      };
    }
  }

  postalCodePayload = (value: string, countryCode?: string) => {
    if (countryCode) {
      return { country_code: countryCode, query: `%${value}%` };
    } else return { query: `%${value}%` };
  };

  filterPostalCodeWithCountry(value: string, update: (el: unknown) => void) {
    update(() => {
      store.dispatch(
        "picklistModule/applyPostalCodeQuery",
        this.postalCodePayload(value, this.country)
      );
    });
  }

  filterPostalCodeManagerWithCountry(
    value: string,
    update: (el: unknown) => void
  ) {
    update(() => {
      store.dispatch(
        "picklistModule/applyPostalCodeQuery",
        this.postalCodePayload(value, this.managerCountry)
      );
    });
  }

  filterPostalCode(value: string, update: (el: unknown) => void) {
    update(() => {
      store.dispatch(
        "picklistModule/applyPostalCodeQuery",
        this.postalCodePayload(value)
      );
    });
  }

  editIdentify() {
    this.checkEditIdentify = !this.checkEditIdentify;
  }

  editSignatureMethod() {
    this.checkEditSignatureMethod = !this.checkEditSignatureMethod;
  }

  editSituation() {
    this.checkEditSituation = !this.checkEditSituation;
  }
  phoneNumber() {
    if (this.party?.person?.contacts && this.contacts) {
      return this.party.person.contacts[0].contactMechanism.phoneNumber !== ""
        ? this.party.person.contacts[0].contactMechanism.phoneNumber
        : this.contacts[0].contactMechanism.phoneNumber;
    }
  }

  addExpandCorrespondent(items: PartyCorrespondent[]) {
    if (items && items.length === 0) {
      const element = store.getters["demandModule/getCorrespondentInit"];
      this.correspondents?.push(element);
    }
  }

  showHideCommunication(value: PartyCorrespondent) {
    value.isDisabled = !value.isDisabled;
    if (value.contactCorrespondent && value.contactCorrespondent.length === 0) {
      add(
        value.contactCorrespondent,
        store.getters["demandModule/getContactCorrespondentInit"]
      );
    }
  }

  searchThird() {
    this.showDialog = true;
    this.openDialog = this.party?.type?.id ?? "party-FrenchPerson";
  }

  searchManager() {
    this.showManagerDialog = true;
    this.openDialog = ThirdType.MANAGER;
  }

  onCloseThird() {
    this.showDialog = false;
    store.dispatch("financingModule/serviceModule/refreshPartyInsurance", {});
  }
  onCloseManager() {
    this.showManagerDialog = false;
    store.dispatch("financingModule/serviceModule/refreshPartyInsurance", {});
  }
  onSelectedItem(row: any) {
    store.dispatch("thirdModule/executeSelectThird", {
      id: row.id,
      type: row._discriminator,
      callback: () => {
        if (this.party) {
          if (row._discriminator === ThirdType.PERSON) {
            this.buildPerson(this.party);
          } else {
            this.buildOrganization(this.party);
            if (this.party.organization?.relations) {
              if (this.manager?.show) this.manager.show = false;
            }
            this.selectedPerson = false;
          }
        }
      },
    });
    this.onCloseThird();
  }
  get typeOutstanding() {
    return store.getters["picklistModule/typeOutstandingItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.organization.typeOutstanding." +
                item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  get financialInstitution() {
    return store.getters["picklistModule/financialInstitutionItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.organization.financialInstitution." +
                item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  get documentType() {
    return store.getters["picklistModule/documentTypeItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.person.identityDoc.docTypes." + item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  get birthPlaceDepartment() {
    const department = this.party?.person?.birthplace?.department;
    const configDepartment = this.party?.person?.birthplace?.type?.config?.department;
    return department === null || configDepartment === null ? "" : department || configDepartment;
  }

  get birthCountryId() {
    return this.party?.person?.birthplace?.type?.config?.country?.id ?? null;
  }

  set birthCountryId(value: string | null) {
    const person: any = this.party?.person;
    if (!person) {
      return;
    }
    this.ensurePersonBirthplaceStructure(person);
    person.birthplace.type.config.country.id = value ?? "";
  }

  private ensurePersonBirthplaceStructure(person: any) {
    if (!person) {
      return;
    }
    if (!person.birthplace) {
      person.birthplace = {};
    }
    if (!person.birthplace.type) {
      person.birthplace.type = {};
    }
    if (!person.birthplace.type.config) {
      person.birthplace.type.config = {
        city: "",
        department: "",
        country: { id: "", label: "" },
      };
    }
    if (!person.birthplace.type.config.country) {
      person.birthplace.type.config.country = { id: "", label: "" };
    }
    const businessDataCountry = person.businessData?.birthplaceCountry;
    if (businessDataCountry && !person.birthplace.type.config.country.id) {
      person.birthplace.type.config.country.id = businessDataCountry;
      person.birthplace.type.config.country.label = businessDataCountry;
      if (person.businessData?.birthplaceCity) {
        person.birthplace.type.config.city = person.businessData.birthplaceCity;
        person.birthplace.type.label = person.businessData.birthplaceCity;
      }
    }
  }

  get isBirthCountryFrance() {
    const countryId = this.party?.person?.birthplace?.type?.config?.country?.id;
    return !countryId || countryId === "FR";
  }

  get birthCityName() {
    const person: any = this.party?.person;
    return person?.businessData?.birthplaceCity ?? "";
  }

  set birthCityName(value: string) {
    const person: any = this.party?.person;
    if (!person) return;
    if (!person.businessData) {
      person.businessData = {};
    }
    person.businessData.birthplaceCity = value;
  }

  private buildPerson(party: Third) {
    party.person = store.state.thirdModule.third?.person;
    if (party.person?.addresses) {
      party.person.addresses = store.state.thirdModule.third?.person?.addresses;
      if (store.state.thirdModule.third?.person?.roles?.length === 0) {
        party.person.roles = [store.getters["demandModule/getRoleInit"]];
      }
    }
  }

  private buildOrganization(party: Third) {
    party.organization = store.state.thirdModule.third?.organization;
    if (party.organization?.addresses) {
      party.organization.addresses =
        store.state.thirdModule.third?.organization?.addresses;
      if (store.state.thirdModule.third?.organization?.roles?.length === 0) {
        party.organization.roles = [store.getters["demandModule/getRoleInit"]];
      }
    }
  }

  onManagerSelected(row: any) {
    store.dispatch("thirdModule/executeSelectThird", {
      id: row.id,
      type: row._discriminator,
      callback: (third: Third) => {
        if (this.manager) {
          this.buildManager(this.manager, third);
        }
      },
    });
    this.showManagerDialog = false;
  }

  private buildManager(manager: Person, third: Third) {
    if (third.person) {
      Object.assign(manager, third.person);
      if (manager.title?.type)
        manager.title.type.value = third.person.title?.type?.value ?? "";
      if (
        manager.contacts &&
        manager.contacts.length > 0 &&
        third.person.contacts &&
        third.person.contacts.length > 0
      ) {
        const contactFound = third.person.contacts
          .filter((contact) =>
            contact.contactMechanism.type?.id?.includes("PHONE_CONTACT")
          )
          .find((contact) => !!contact.contactMechanism);
        if (contactFound) {
          manager.contacts[0].contactMechanism.phoneNumber =
            contactFound?.contactMechanism.phoneNumber;
        } else {
          manager.contacts.unshift(this.phoneContact);
        }
      } else if (manager.contacts) {
        manager.contacts.push(
          store.getters["demandModule/getContactMechanismInit"]
        );
      }
      if (third.person.roles?.find((el) => el.role?.type?.value === "DIRG")) {
        manager.roles = third.person.roles;
      } else {
        third.person.roles?.forEach((el) => manager?.roles?.push(el));
      }
    }
  }

  addExpandJobs(items: Jobs[]) {
    if (items && items.length === 0) {
      const element = store.getters["demandModule/getThirdJobsInit"];
      items.push(element);
    }
  }

  addExpandSituation(items: PersonMaritalStatus[]) {
    if (items && items.length === 0) {
      const element = store.getters["demandModule/getSituationInit"];
      items.push(element);
    }
  }

  removeExpandedJobs(items: Jobs[]) {
    if (!!items && items.length === 1) {
      items.slice(0, 1);
    }
  }

  unshiftMeansOfCommunication<T>(items: T[], getter: T) {
    items.unshift(getter);
  }

  removeBirthChildren(index: number) {
    if (this.party?.person?.birthChildren && this.party?.person?.nbOfChildren) {
      this.party.person.birthChildren.splice(index, 1);
      this.party.person.nbOfChildren--;
    }
  }
  getSirenMask(locale?: string) {
    return getSirenMask(locale);
  }
  updateBirthChildren() {
    logger.debug('childrenchanged--------------')
    if (this.party?.person?.birthChildren && this.party?.person?.nbOfChildren) {
      if (
        this.party.person.birthChildren.length < this.party.person.nbOfChildren
      ) {
        const maximum = Number(
          this.party.person.nbOfChildren -
          this.party.person.birthChildren.length
        );
        for (let i = 0; i < maximum; i++) {
          this.party.person.birthChildren.push({
            dateOfBirthChildren: "",
            childrenAge: undefined,
          });
        }
      } else if (
        this.party.person.birthChildren.length > this.party.person.nbOfChildren
      ) {
        this.party.person.birthChildren.splice(this.party.person.nbOfChildren);
      }
    }
  }

  disableWhenExistOfferRefAndContactResourceUid(
    offerUid: string,
    contactUid: string
  ) {
    return !!offerUid && !!contactUid;
  }

  get opinionQualifications() {
    return store.getters["taskModule/analysisModule/getOpinionQualifications"];
  }

  get getColumnsRevenusMensuels() {
    return globalConfig.table_config.colsRevenusMensuels;
  }

  orderDetail(detail: ExpenseIncome[]) {
    return detail;
  }

  removeDetail(
    detail: ExpenseIncome[],
    index: number,
    code: string,
    indexAnalysis: number
  ) {
    const details = this.getDetails(detail, code);
    const detailsDifferent = this.getDetailsDifferent(detail, code);
    details.splice(index, 1);
    Array.prototype.push.apply(details, detailsDifferent);
    if (this.party && this.party.person && this.party.person.revenues) {
      this.party.person.revenues[indexAnalysis].detail = details;
    }
  }

  getDetails(detail: ExpenseIncome[], code: string) {
    return detail.filter((el: any) => el.type === code);
  }

  getDetailsDifferent(detail: ExpenseIncome[], code: string) {
    return detail.filter((el: any) => el.type !== code);
  }

  getNatures(code: string) {
    return store.getters["picklistModule/natureItems"]
      ?.filter((item: any) => item.config.internalCode === code)
      .map((item: any) => {
        return {
          ...item,
          label:
            item.label?.toString() !== ""
              ? i18n.global.t("incomeAndExpenses.type." + item.value)
              : "",
        };
      });
  }

  applyLldEncoursGlobal() {
    if (!this.exposuresList?.length) return;
    const offer = resolveOfferForLld();
    const proposalItem = getSelectedProposalItem(offer) as SimpleQuote;
    if (!isLldFinancingProduct(proposalItem?.financingProduct)) return;
    const partyRole = this.role?.role_code
      || this.role?.associatedParty?.role_code
      || getPartyRoleFromOffer(offer, this.party?.person?.resourceUid || this.party?.resourceUid);
    this.exposuresList.forEach((exposure: any) => {
      if (isEncoursAxaGlobal(exposure)) {
        applyLldAxaGlobalEncours(
          exposure,
          this.expossureList,
          proposalItem,
          offer,
          partyRole,
          offer?.reference || this.offer?.reference
        );
      }
    });
  }

  get exposuresList() {
    return this.expossureList?.filter(
      (exposure: any) => (exposure.contractReference === this.offer?.reference && !exposure.hidden)
    );
  }

  get gender() {
    return store.getters["picklistModule/genderItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.person.identity.gender." + item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  get socioProfessionalCategory() {
     return store.getters["picklistModule/jobOccupationTypeItems"]
         ?.map((item: any) => {
           return {
             ...item,
             label:
                 item.value?.toString() !== ""
                     ? `${item.value} - ${i18n.global.t(
                         "picklists.socioProfessionalCode.".concat(item.value)
                     )}`
                     : "",
           };
         })
         .sort((a: any, b: any) => {
                 return a.label.localeCompare(b.label);
         });

  }

  get contractTypes() {
    return store.getters["picklistModule/contractTypeItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.person.occupation.occupationTypes." +
                item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  get propertyStatus() {
    return store.getters["picklistModule/propertyStatusItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.person.address.livingstatus." + item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  /**
   * Get the nace code options with translated labels.
   *
   * @return {Array<ComboBoxItem>} The translated nace code options.
   */
  get naceOptions() {
    return store.getters["picklistModule/codeNaceOptionsItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? `${item.value} - ${i18n.global.t(
                "picklists.codeNace.".concat(item.value)
              )}`
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  get qualificationTypeGroupeOptions() {
    return store.getters["picklistModule/qualificationTypeGroupeOptionsItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t(
                "picklists.qualificationTypeGroupe." + item.value
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
 
  
   /* return [ {label: "Formel", value: "FORMEL"},
     {label: "Informel intégré", value: "INFORMEL_INTEGRE"},
     {label: "Informel non intégré", value: "INFORMEL_NON_INTEGRE"}
     ].sort((a: any, b: any) => a.label.localeCompare(b.label));*/
  }
  addNewDetail(detail: ExpenseIncome[], type: string) {
    let element = {};
    if (type === "EXPENSE") {
      element = store.getters["demandModule/getExpenseInit"];
    } else {
      element = store.getters["demandModule/getIncomeInit"];
    }
    detail.push(element);
  }

  addAnalysis() {
    const element = store.getters["demandModule/getThirdAnalysisInit"];
    this.analysis?.push(element);
  }

  editAnalysis(value: { isDisabled: boolean }) {
    value.isDisabled = !value.isDisabled;
  }

  removeAnalysis<T>(index: number, items: T[]) {
    if (items && items.length > 0) {
      items.splice(index, 1);
    }
  }

  getNatureIncome() {
    return this.getNatures("INCOME");
  }
  getNatureExpense() {
    return this.getNatures("EXPENSE");
  }

  showHideDetail(value: RevenuesAnalysis) {
    value.isDisabled = !value.isDisabled;
  }

  isValidEmail(val: any) {
    return globalConfig.input_rules.isValidEmail(val);
  }

  checkKeyDownAlpha(event: any) {
    return globalConfig.input_rules.checkKeyDownAlpha(event);
  }

  validateBirthDate(birthDate: string) {
    return globalConfig.input_rules.validateBirthDate(birthDate);
  }

  async dispatchUpdatePartyInsurance() {
    await store.dispatch(
      "financingModule/serviceModule/refreshPartyInsurance",
      {}
    );
    await store.dispatch(
      "financingModule/serviceModule/calculatePartyInsurance"
    );
  }
  changeSignature(event: any) {
    logger.debug(event);
    store.dispatch("demandModule/setSignatureMethodAnalysis",event)

  }

  get isManager() {
    return this.associatedParty?.role?.resourceUid === "DIRG";
  }

  get isManagerDisabled() {
    return !this.checkEditButtonManager;
  }

  get manager() {
    if (this.offer?.resourceUid && this.party?.organization?.manager) {
      this.party.organization.manager.show = false;
    }
    return this.party?.organization?.manager;
  }

  get disablePartySelection() {
    return (
      this.offer?.resourceUid !== "" &&
      this.associatedParty?.role?.resourceUid === "CUSTOMER"
    );
  }

  /* Picklists */
  get periodicities() {
    return store.getters["picklistModule/periodicityItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.label?.toString() !== ""
            ? i18n.global.t("incomeAndExpenses.periodicity." + item.value)
            : "",
      };
    });
  }

  get clientType() {
    return store.getters["picklistModule/clientTypeItems"]?.map((item: any) => {
      return {
        ...item,
        label:
          item.id?.toString() !== ""
            ? i18n.global.t("enum.party.type." + item.id)
            : "",
      };
    });
  }

  get legalCategory() {
    return store.getters["picklistModule/legalCategoryItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? `${item.value} - ${i18n.global.t(
              `picklists.legal-category.${item.value}`
            )}`
            : "",
      };
    });
  }

  get activitySector() {
    return store.getters["picklistModule/activitySectorItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? `${item.value} - ${i18n.global.t(
              "picklists.activity.".concat(item.value)
            )}`
            : "",
      };
    });
  }

  get countries() {
    return store.getters["picklistModule/countriesItems"];
  }

  get statusesMarital() {
    return store.getters["picklistModule/statusMaritalItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.statusMarital." + item.value)
            : "",
      };
    });
  }
  isPartySupplierOrDistributor() {
    return !customRoles.includes(this.role?.role_code ? this.role?.role_code : this.role?.associatedParty?.role_code)
  }
  get title() {
    return store.getters["picklistModule/titlesItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.title." + item.value)
            : "",
      };
    });
  }

  get signatureMethods() {
    return store.getters["picklistModule/signatureMethodsItems"]?.map(
      (item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t("demand.party.signatureMethod." + item.value)
              : "",
        };
      }
    );
  }

  get qualificationType() {
    return store.getters["picklistModule/qualificationTypeItems"].map(
      (item: any) => {
        return {
          ...item,
          label:
            item.label?.toString() !== ""
              ? i18n.global.t(
                "middleOffice.party.organization.contact.fonction." +
                item.value
              )
              : "",
        };
      }
    );
  }

  get contactType() {
    return store.getters["picklistModule/contactTypeItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.id?.toString() !== ""
            ? i18n.global.t("demand.party.contactMechanism.options." + item.id)
            : "",
      };
    });
  }
  get addressPreference() {
    return store.getters["picklistModule/addressPreferenceTypeItems"].map((item: any) => {
      return {
        ...item,
        label: item.label?.toString() !== "" ? i18n.global.t("demand.party.addressPreferenceType." + item.label) : ''
      };
    }).sort((a: any, b: any) =>
      a.label.localeCompare(b.label)
    )
  } get addressType() {
    return store.getters["picklistModule/addressTypeItems"];
  }

  get addressPreferenceType() {
    const types = store.getters["picklistModule/addressPreferenceTypeItems"]
      .map((item: any) => {
        return {
          ...item,
          label:
            item.label?.toString() !== ""
              ? i18n.global.t(
                "demand.party.addressPreferenceType." + item.label
              )
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    return types?.filter((item: any) => item.value !== "SIEGE");
  }

  get postalCodes() {
    return store.getters["picklistModule/postalCodesItems"];
  }

  get offer() {
    return store.state.demandModule.offer;
  }

  get contacts() {

    return this.party?.person?.contacts?.sort((a, b) => {
      if (
        a.contactMechanism &&
        a.contactMechanism.type?.id === "PHONE_CONTACT" &&
        b.contactMechanism &&
        b.contactMechanism.type?.id !== "PHONE_CONTACT"
      ) {
        return -1;
      } else if (
        b.contactMechanism &&
        b.contactMechanism.type?.id === "PHONE_CONTACT" &&
        a.contactMechanism &&
        a.contactMechanism.type?.id !== "PHONE_CONTACT"
      ) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  get nationality() {
    return store.getters["picklistModule/nationalityItems"]
      ?.map((item: any) => {
        return {
          ...item,
          label:
            item.value?.toString() !== ""
              ? i18n.global.t("demand.party.country." + item.value)
              : "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }
  get geographicalZoneItems() {
    return store.getters["picklistModule/geographicZoneItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.geographicZone." + item.value)
            : "",
      };
    });
  }
  get personJobs() {
    return this.party?.person?.jobs;
  }

  get requiredField() {
    return i18n.global.t("main.requiredField");
  }
  get isQualificationTypeGroupeRequired() {
    return this.currentRoute === "TaskDetail" && this.isPartySupplierOrDistributor();
  }

  get jobActivity() {
    return store.getters["picklistModule/jobActivityItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.jobActivity." + item.value)
            : "",
      };
    });
  }

  get jobPositionType() {
    return store.getters["picklistModule/jobPositionTypeItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.positionType." + item.value)
            : "",
      };
    });
  }

  get jobOccupationType() {
    return store.getters["picklistModule/jobOccupationTypeItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.occupationType." + item.value)
            : "",
      };
    });
  }

  get matrimonialRegime() {
    return store.getters["picklistModule/matrimonialRegimeItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.regimeMatrimonial." + item.value)
            : "",
      };
    });
  }

  get relationTypes() {
    return store.getters["picklistModule/partyRelationTypeItems"].map((item: any) => {
      return {
        ...item,
        label:
          item.value?.toString() !== ""
            ? i18n.global.t("demand.party.relationType." + item.value)
            : "",
      };
    });
  }

  get analysis() {
    return this.party?.person?.revenues;
  }

  get streetLabel() {
    return i18n.global.t("demand.party.label.street");
  }

  get AdressLine1Label() {
    return i18n.global.t("demand.party.label.adressLine1Label");
  }

  get AdressLine2Label() {
    return i18n.global.t("demand.party.label.adressLine2Label");
  }

  get AdressLine3Label() {
    return i18n.global.t("demand.party.label.adressLine3Label");
  }

  verifyKSIOPCombination(financialInstitution: string, outstandingType: string) {
    if (financialInstitution) {
      return true
    } else {
      return false
    }
  }

  changePostal(c: any) {
    if (this.c.businessData) {
      this.c.businessData.zipCode = c.zipCode;
      this.c.businessData.city = c.city;
    }
  }
  getCodeCity(value: any): string {
    if (!value?.businessData?.zipCode || !value?.businessData?.city) {
      return '';
    }
    return `${value.businessData.zipCode} - ${value.businessData.city}`;
  }

  recalculateKSIOP(value: any, index: number) {
    store.dispatch("demandModule/recalculateKSIOP", {
      associatedPartyIndex: this.associatedPartyId,
      financial: value,
      currency: this.currencyConfiguration.currency,
      callback: (result: any) => {
        if (this.party?.type?.id == ThirdType.PERSON) {
          this.party?.person?.financialExposures ? this.party.person.financialExposures[index].businessData = result.businessData : ''
        } else {
          this.party?.organization?.financialExposures ? this.party.organization.financialExposures[index].businessData = result.businessData : ''
        }
      },
    });
  }


}
